import { useQuery } from '@apollo/client'
import { Dictionary, groupBy, sortBy } from 'lodash'
import { useParams } from 'react-router-dom'
import { AlbumsReadAllDocument, AlbumV2 } from '../../../../../graphql/generated'
import { authenticatedUserVar } from '../../../../../providers/apollo/cache'

export interface AlbumTree extends AlbumV2 {
  children: AlbumTree[]
}

export interface GetAlbums {
  allAlbums: AlbumV2[]
  groupedAlbums: Dictionary<AlbumV2[]>
  currentAlbum?: AlbumV2
  currentIsRoot: boolean
  currentSubAlbums: AlbumV2[]
  rootAlbum?: AlbumV2
  loading: boolean
}

export function getAlbumTree(albums: AlbumV2[], pinnedAlbums: (string | null | undefined)[]) {
  let root: AlbumTree | undefined
  const indexMap: { [key: string]: number } = {}
  const orphans: AlbumTree[] = []
  const nodes: AlbumTree[] = albums
    .filter((album) => !pinnedAlbums.includes(album.id))
    .map((album, index) => {
      indexMap[album.id] = index
      return { ...album, children: [] }
    })

  nodes.forEach((node) => {
    // Set the root of tree - My Bucket
    if (node.id === node.parentAlbumId || node.parentAlbumId === null) {
      root = node
      return
    }

    // Retrieve parent node
    if (node.parentAlbumId) {
      const indexOfParent = indexMap[node.parentAlbumId]
      const parent = nodes[indexOfParent]

      if (parent) {
        // Add currrent node to parent's children
        parent.children = [...parent.children, node]
      } else {
        orphans.push(node)
      }
    }
  })

  if (root) {
    // Add orphaned albums to root
    root.children = [...root.children, ...orphans]
  }

  return root
}

export default function useGetAlbums() {
  const variables = { sortBy: { desc: false } }
  const { albumId } = useParams<{ albumId?: string }>()
  const user = authenticatedUserVar()
  const { data, loading } = useQuery(AlbumsReadAllDocument, { variables })
  const pinnedAlbums = [user?.profile?.mobileAlbum]

  // Set default to empty array to prevent reference error on initial render
  const allAlbums = data?.albumsReadAll ? (data?.albumsReadAll as AlbumV2[]) : []
  const sortedAllAlbums = sortBy(allAlbums, 'title')

  // Group albums by parent id
  const groupedAlbums = groupBy(sortedAllAlbums, (album) => (album.parentAlbumId ? album.parentAlbumId : 'parent'))

  // Determine the current album being displayed - If no album id is in the path then must be root album
  const currentAlbum = allAlbums.find((album) => albumId === album?.id) || allAlbums.find((album) => album.id === user?.profile?.defaultAlbum)

  // Make it easy for components to get the sub albums of the current album
  const currentSubAlbums = currentAlbum && groupedAlbums[currentAlbum.id] ? groupedAlbums[currentAlbum.id] : []

  // Make it easy for components to know if current album is root
  const currentIsRoot = Boolean(currentAlbum && currentAlbum.id === user?.profile?.defaultAlbum)

  const rootAlbum = allAlbums.find((album) => album.id === album.parentAlbumId || album.parentAlbumId === null)

  return { allAlbums, groupedAlbums, currentAlbum, currentIsRoot, currentSubAlbums, rootAlbum, loading, pinnedAlbums }
}
